import { useEffect, useRef } from "react";
import { useForm } from "@core/features/form";
import { Form } from "@themes/pollinator/ui/Form";
import { FormField } from "@themes/pollinator/ui/FormField";
import { Button } from "@themes/pollinator/ui/Button";
import { Type } from "@themes/pollinator/ui/Type";
import { marketingFields } from "../../marketing.type";
import { withLinks } from "../../utils/withLinks";
import { newsletterSchema, type NewsletterFormData } from "./newsletter.schema";

/** What the page says once somebody has subscribed. */
export type NewsletterConfirmation = { heading: string; body: string };

/** Said when the form has no wording of its own. */
const DEFAULT_CONFIRMATION: NewsletterConfirmation = {
  heading: "Thank you for subscribing.",
  body: "We'll keep you updated on our latest news and opportunities.",
};

export type Props = {
  onSubmit: (data: NewsletterFormData) => Promise<void>;
  /**
   * The form's configured wording for after it is sent, as the submission
   * returned it. Each line of the body is a paragraph.
   */
  confirmation?: NewsletterConfirmation;
  /** Called once, when half the form has come onto the screen */
  onSeen?: () => void;
  /** Called once, when something is first typed into it */
  onStart?: () => void;
  className?: string;
};

/**
 * Get Updates, on the page rather than behind a button.
 *
 * The design puts this form at the foot of the homepage, so somebody can
 * subscribe without first deciding to open anything.
 */
export function MarketingNewsletterForm({
  onSubmit,
  onSeen,
  onStart,
  confirmation,
  className,
}: Props) {
  const { form, handleSubmit } = useForm({
    schema: newsletterSchema,
    onSubmit,
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      confirmEmail: "",
    },
  });

  // The form sits at the foot of a long page, so being rendered is not being
  // seen: most visitors never scroll this far. It is reported once half of
  // it is on screen, as the page's sections are.
  const root = useRef<HTMLDivElement>(null);
  const seen = useRef(false);
  useEffect(() => {
    if (!onSeen || !root.current || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (seen.current || !entries.some((entry) => entry.isIntersecting)) return;
        seen.current = true;
        onSeen();
        observer.disconnect();
      },
      { threshold: 0.5 }
    );
    observer.observe(root.current);
    return () => observer.disconnect();
  }, [onSeen]);

  // Only a change somebody made counts as starting. The form sets values of
  // its own when it loads.
  const started = useRef(false);
  useEffect(() => {
    const subscription = form.watch((_values, change) => {
      // Changing the address after the two matched has to say so, under the
      // confirmation, where the complaint about a mismatch is made.
      if (change.name === "email" && form.getValues("confirmEmail")) {
        void form.trigger("confirmEmail");
      }
      if (change.type !== "change" || started.current) return;
      started.current = true;
      onStart?.();
    });
    return () => subscription.unsubscribe();
  }, [form, onStart]);

  const { isSubmitting, isValid, isSubmitSuccessful } = form.formState;

  if (isSubmitSuccessful) {
    const { heading, body } = confirmation ?? DEFAULT_CONFIRMATION;
    return (
      <div className={className} data-testid="newsletter-form-success">
        <Type variant="h4">{heading}</Type>
        {body
          .split("\n")
          .filter((line) => line.trim())
          .map((line) => (
            <Type variant="body2" key={line}>
              {withLinks(line)}
            </Type>
          ))}
      </div>
    );
  }

  return (
    <div className={className} ref={root}>
      <Form form={form}>
        {/* Paired columns, as the design lays the card out */}
        {/* Field metrics the design fixes and the shared inputs do not: 44px
            inputs, 122px textareas that do not resize, 16px olive placeholders
            set 14px in, labels 6px above an input and 12px above a textarea,
            and no asterisks: every field but the last two is required, and
            the form says so when one is missed. */}
        <div className="grid grid-cols-1 gap-x-5 gap-y-5 md:grid-cols-2 md:gap-x-4 md:gap-y-4 lg:gap-x-5 lg:gap-y-5 [&>div]:gap-1.5 [&>div:has(textarea)]:gap-3 [&_input]:px-3.5 [&_input]:text-[16px] [&_input+span]:left-3.5 [&_input+span]:top-[6px] [&_input+span]:text-[16px] [&_input+span]:text-[#606800] [&_textarea]:px-3.5 [&_textarea]:py-3 [&_textarea]:text-[16px] [&_textarea]:leading-6 [&_input]:h-11 [&_input]:shadow-none [&_textarea]:h-[122px] [&_textarea]:resize-none [&_textarea]:shadow-none [&_input]:placeholder:text-[#606800] [&_textarea]:placeholder:text-[#606800]">
        <FormField
          className={marketingFields.noMark}
          name="firstName"
          label="First Name"
          component="Input"
          placeholder="Enter your first name"
          required
        />
        <FormField
          className={marketingFields.noMark}
          name="lastName"
          label="Last Name"
          component="Input"
          placeholder="Enter your last name"
          required
        />
        <FormField
          className={marketingFields.noMark}
          name="email"
          label="Email"
          component="Input"
          placeholder="Enter your email"
          autoComplete="email"
          required
        />
        <FormField
          className={marketingFields.noMark}
          name="confirmEmail"
          label="Email Confirmation"
          component="Input"
          placeholder="Enter your email again"
          required
        />
        </div>
      </Form>
      {/* 24px under the last fields, as the design spaces the button */}
      <div className="pt-4 md:pt-5 lg:pt-6">
        <Button
          onClick={handleSubmit}
          disabled={isSubmitting}
          // Looks disabled until the form will pass, and still takes the
          // press: that is what shows the errors on fields nobody touched.
          visuallyDisabled={!isSubmitting && !isValid}
          isLoading={isSubmitting}
          className="w-full h-9 lg:w-64 lg:h-11"
          typeClassName="text-[14px] leading-[20px] md:text-[14px] md:leading-[20px] lg:text-[16px] lg:leading-[24px] font-medium"
          data-testid="newsletter-form-submit"
        >
          Subscribe
        </Button>
      </div>
    </div>
  );
}

export default MarketingNewsletterForm;
